import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  Calendar,
  Copy,
  Link2,
  Lock,
  MapPin,
  MoreHorizontal,
  Tag,
  Trash2,
  User,
  Wrench,
} from 'lucide-react';
import { PDFViewerState, PdfPageProxy, PdfRenderTask } from '../../types';

/**
 * Local alias that surfaces the optional `cleanup()` method from PDF.js
 * so we can release page-level memory after rendering completes.
 */
type PdfPageProxyWithCleanup = PdfPageProxy & { cleanup?: () => void };
import { usePdfDocumentQuery, usePdfRuntimeQuery } from '../../hooks/usePDFQuery';
import { PDFLoadingState } from '../shared/PDFLoadingState';
import { PDFErrorState } from '../shared/PDFErrorState';
import { Layer, ObservationRef, PunchListItemRef, SubmittalRef, RFIRef } from '../../api';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { PRIORITY_LABELS as OBS_PRIORITY_LABELS, STATUS_LABELS as OBS_STATUS_LABELS } from '@/features/observations';
import { PRIORITY_LABELS as PUNCH_PRIORITY_LABELS, STATUS_LABELS as PUNCH_STATUS_LABELS } from '@/features/punch-lists';
import { SUBMITTAL_PRIORITY_LABELS, SUBMITTAL_STATUS_LABELS } from '@/features/submittals';
import { RFI_PRIORITY_LABELS, RFI_STATUS_LABELS } from '@/features/rfis';
import { pickSafeRenderScale } from '@ironview/interactive-viewer';

/**
 * Desired render resolution multiplier on capable devices. The actual
 * render scale is clamped by `pickSafeRenderScale` based on the page
 * size and device limits, so large architectural drawings don't blow
 * past iOS Safari's ~16 MP canvas cap (which manifests as the
 * "A problem repeatedly occurred" crash).
 */
const DESIRED_RENDER_SCALE = 2;
/**
 * How long the zoom level must hold still before we re-rasterise the
 * page at the new scale. Wheel ticks and pinch frames change the scale
 * dozens of times per second; re-rendering on each one would thrash the
 * main thread, so we wait for the gesture to settle.
 */
const RENDER_SETTLE_MS = 1000;
const SELECTION_PADDING = 8;
const OBS_STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800 border-blue-200',
  COMPLETED: 'bg-green-100 text-green-800 border-green-200',
  OVERDUE: 'bg-red-100 text-red-800 border-red-200',
};
const OBS_PRIORITY_COLORS: Record<string, string> = {
  LOW: 'text-blue-700 border-blue-200',
  MEDIUM: 'text-amber-700 border-amber-200',
  HIGH: 'text-red-700 border-red-200',
  URGENT: 'text-red-800 border-red-300',
};
const PUNCH_STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800 border-blue-200',
  COMPLETED: 'bg-green-100 text-green-800 border-green-200',
  OVERDUE: 'bg-red-100 text-red-800 border-red-200',
  CLOSED: 'bg-gray-100 text-gray-700 border-gray-200',
};
const PUNCH_PRIORITY_COLORS: Record<string, string> = {
  LOW: 'text-blue-700 border-blue-200',
  MEDIUM: 'text-amber-700 border-amber-200',
  HIGH: 'text-red-700 border-red-200',
};
const SUBMITTAL_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-800 border-slate-200',
  OPEN: 'bg-blue-100 text-blue-800 border-blue-200',
  IN_REVIEW: 'bg-violet-100 text-violet-800 border-violet-200',
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  APPROVED_WITH_COMMENTS: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  REVISE_RESUBMIT: 'bg-amber-100 text-amber-800 border-amber-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
  CLOSED: 'bg-gray-100 text-gray-700 border-gray-200',
};
const SUBMITTAL_PRIORITY_COLORS: Record<string, string> = {
  LOW: 'text-blue-700 border-blue-200',
  MEDIUM: 'text-amber-700 border-amber-200',
  HIGH: 'text-red-700 border-red-200',
  CRITICAL: 'text-red-800 border-red-300',
};
const RFI_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-800 border-slate-200',
  OPEN: 'bg-blue-100 text-blue-800 border-blue-200',
  PENDING_RESPONSE: 'bg-violet-100 text-violet-800 border-violet-200',
  ANSWERED: 'bg-green-100 text-green-800 border-green-200',
  CLOSED: 'bg-gray-100 text-gray-700 border-gray-200',
};
const RFI_PRIORITY_COLORS: Record<string, string> = {
  LOW: 'text-blue-700 border-blue-200',
  NORMAL: 'text-amber-700 border-amber-200',
  HIGH: 'text-red-700 border-red-200',
  URGENT: 'text-red-800 border-red-300',
};

const wrapTextToWidth = (
  value: string,
  width: number,
  fontSize: number
): string[] => {
  const normalized = value.replace(/\r\n/g, '\n');
  const paragraphs = normalized.split('\n');
  const safeFontSize = Math.max(8, fontSize);
  const maxTextWidth = Math.max(1, width - 12); // 6px padding on each side
  // Good approximation for UI fonts in this app.
  const avgCharWidth = safeFontSize * 0.56;
  const maxChars = Math.max(1, Math.floor(maxTextWidth / avgCharWidth));
  const wrapped: string[] = [];

  for (const paragraph of paragraphs) {
    const raw = paragraph.trimEnd();
    if (!raw) {
      wrapped.push('');
      continue;
    }

    const words = raw.split(/\s+/);
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (candidate.length <= maxChars) {
        line = candidate;
      } else {
        if (line) wrapped.push(line);
        if (word.length <= maxChars) {
          line = word;
        } else {
          // Hard-break long tokens (URLs, IDs) so they still respect box width.
          let index = 0;
          while (index < word.length) {
            wrapped.push(word.slice(index, index + maxChars));
            index += maxChars;
          }
          line = '';
        }
      }
    }
    if (line) wrapped.push(line);
  }

  return wrapped.length ? wrapped : [''];
};

interface PDFCanvasViewportProps {
  fileUrl: string;
  viewerState: PDFViewerState;
  onLoadSuccess: (numPages: number) => void;
  onScaleChange: (nextScale: number) => void;
  onPositionChange: (position: { x: number; y: number }) => void;
  /** Called once after the first page loads with the fit scale and centered position. */
  onFitComplete?: (fitScale: number, fitPosition: { x: number; y: number }) => void;
  /** Called when the PDF's intrinsic dimensions are known. */
  onDimensionsLoad?: (dimensions: { width: number; height: number }) => void;
  /** Called after the first raster is painted and ready to reveal. */
  onInitialRenderComplete?: () => void;
  /** Called when initial loading reaches an error state. */
  onInitialRenderError?: () => void;
  /**
   * Called when the user does a true click on the background (mousedown → mouseup with no drag).
   * Receives the clientX/Y of the click so callers can convert to content coordinates.
   */
  onBackgroundClick?: (pdfX: number, pdfY: number) => void;
  /** Text shown in the loading state. Defaults to "Loading Drawing...". */
  loadingMessage?: string;
  /**
   * Optional crop region in PDF points [x, y, width, height].
   * When set, only this region of the page is rendered and visible.
   * Dimensions reported to onDimensionsLoad will reflect the crop size.
   */
  cropRegion?: [number, number, number, number] | null;
  flippedHorizontal?: boolean;
  flippedVertical?: boolean;
  layers?: Layer[];
  selectedLayerIds?: string[];
  onLayerClick?: (layerId: string, options?: { toggle?: boolean; exclusive?: boolean }) => void;
  onLayerChange?: (layerId: string, nextData: Record<string, unknown>) => void;
  onSelectedLayerLink?: () => void;
  onSelectedLayerDelete?: () => void;
  onSelectedLayerDuplicate?: () => void;
  selectedTextDraft?: string;
  selectedTextFontSize?: number;
  onSelectedTextDraftChange?: (value: string) => void;
  onSelectedTextFontSizeChange?: (value: number) => void;
  onSelectedTextCommit?: () => void;
  punchLookup?: Record<string, PunchListItemRef>;
  observationLookup?: Record<string, ObservationRef>;
  submittalLookup?: Record<string, SubmittalRef>;
  rfiLookup?: Record<string, RFIRef>;
  onPunchLabelClick?: (punchItemId: string) => void;
  onObservationLabelClick?: (observationId: string) => void;
  onSubmittalLabelClick?: (submittalId: string) => void;
  onRfiLabelClick?: (rfiId: string) => void;
  onHyperlinkNavigate?: (target: { pdfFileId: string; pageNumber: number; referenceToken?: string }) => void;
  searchHighlights?: Array<{
    points: Array<{ x: number; y: number }>;
    active?: boolean;
  }>;
  searchFocusRequestNonce?: number;
  drawingEnabled?: boolean;
  drawingShapeType?: 'select' | 'cloud' | 'arrow' | 'textbox' | 'pin';
  onShapeDrawComplete?: (
    start: { x: number; y: number },
    end: { x: number; y: number }
  ) => void;
}

export const PDFCanvasViewport: React.FC<PDFCanvasViewportProps> = ({
  fileUrl,
  viewerState,
  onLoadSuccess,
  onScaleChange,
  onPositionChange,
  onFitComplete,
  onDimensionsLoad,
  onInitialRenderComplete,
  onInitialRenderError,
  onBackgroundClick,
  loadingMessage = 'Loading Drawing...',
  cropRegion = null,
  flippedHorizontal = false,
  flippedVertical = false,
  layers = [],
  selectedLayerIds = [],
  onLayerClick,
  onLayerChange,
  onSelectedLayerLink,
  onSelectedLayerDelete,
  onSelectedLayerDuplicate,
  selectedTextDraft,
  selectedTextFontSize,
  onSelectedTextDraftChange,
  onSelectedTextFontSizeChange,
  onSelectedTextCommit,
  punchLookup = {},
  observationLookup = {},
  submittalLookup = {},
  rfiLookup = {},
  onPunchLabelClick,
  onObservationLabelClick,
  onSubmittalLabelClick,
  onRfiLabelClick,
  onHyperlinkNavigate,
  searchHighlights = [],
  searchFocusRequestNonce,
  drawingEnabled = false,
  drawingShapeType = 'cloud',
  onShapeDrawComplete,
}) => {
  type ResizeCorner = 'tl' | 'tr' | 'br' | 'bl';
  type LayerKind = 'box' | 'line';

  const getCloudPath = useCallback((x: number, y: number, width: number, height: number) => {
    // Rectangular cloud markup with fully symmetrical scalloped edges.
    const w = Math.max(36, width);
    const h = Math.max(28, height);
    const left = x;
    const top = y;
    const right = x + w;
    const bottom = y + h;

    const scallop = Math.max(5, Math.min(12, Math.min(w, h) * 0.1));
    const cornerInset = scallop;

    const usableTop = Math.max(8, w - cornerInset * 2);
    const usableSide = Math.max(8, h - cornerInset * 2);
    const topSteps = Math.max(3, Math.round(usableTop / (scallop * 2)));
    const sideSteps = Math.max(2, Math.round(usableSide / (scallop * 2)));

    const topStartX = left + cornerInset;
    const topEndX = right - cornerInset;
    const sideStartY = top + cornerInset;
    const sideEndY = bottom - cornerInset;

    const path: string[] = [`M ${topStartX} ${top}`];

    // Top edge
    for (let i = 0; i < topSteps; i += 1) {
      const startX = topStartX + (i * (topEndX - topStartX)) / topSteps;
      const endX = topStartX + ((i + 1) * (topEndX - topStartX)) / topSteps;
      const midX = (startX + endX) / 2;
      path.push(`Q ${midX} ${top - scallop} ${endX} ${top}`);
    }

    // Top-right corner turn
    path.push(`Q ${right} ${top} ${right} ${sideStartY}`);

    // Right edge
    for (let i = 0; i < sideSteps; i += 1) {
      const startY = sideStartY + (i * (sideEndY - sideStartY)) / sideSteps;
      const endY = sideStartY + ((i + 1) * (sideEndY - sideStartY)) / sideSteps;
      const midY = (startY + endY) / 2;
      path.push(`Q ${right + scallop} ${midY} ${right} ${endY}`);
    }

    // Bottom-right corner turn
    path.push(`Q ${right} ${bottom} ${topEndX} ${bottom}`);

    // Bottom edge
    for (let i = 0; i < topSteps; i += 1) {
      const startX = topEndX - (i * (topEndX - topStartX)) / topSteps;
      const endX = topEndX - ((i + 1) * (topEndX - topStartX)) / topSteps;
      const midX = (startX + endX) / 2;
      path.push(`Q ${midX} ${bottom + scallop} ${endX} ${bottom}`);
    }

    // Bottom-left corner turn
    path.push(`Q ${left} ${bottom} ${left} ${sideEndY}`);

    // Left edge
    for (let i = 0; i < sideSteps; i += 1) {
      const startY = sideEndY - (i * (sideEndY - sideStartY)) / sideSteps;
      const endY = sideEndY - ((i + 1) * (sideEndY - sideStartY)) / sideSteps;
      const midY = (startY + endY) / 2;
      path.push(`Q ${left - scallop} ${midY} ${left} ${endY}`);
    }

    // Top-left corner turn
    path.push(`Q ${left} ${top} ${topStartX} ${top}`);

    path.push('Z');
    return path.join(' ');
  }, []);

  const clientToPdfPoint = useCallback((clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    return {
      x: (clientX - rect.left - viewerState.position.x) / viewerState.scale,
      y: (clientY - rect.top - viewerState.position.y) / viewerState.scale,
    };
  }, [viewerState.position.x, viewerState.position.y, viewerState.scale]);

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<PdfRenderTask | null>(null);
  const textEditorRef = useRef<HTMLTextAreaElement | null>(null);
  const lastAutofocusLayerIdRef = useRef<string | null>(null);

  const [pdfDimensions, setPdfDimensions] = useState<{ width: number; height: number } | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [drawStartPoint, setDrawStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrentPoint, setDrawCurrentPoint] = useState<{ x: number; y: number } | null>(null);
  const [draftLayerData, setDraftLayerData] = useState<Record<string, Record<string, unknown>>>({});
  const [interactionState, setInteractionState] = useState<{
    layerId: string;
    mode: 'move' | 'resize-corner';
    corner?: ResizeCorner;
    kind: LayerKind;
    moveLayerIds?: string[];
    initialDataById?: Record<string, Record<string, unknown>>;
    startClient: { x: number; y: number };
    initialData: Record<string, unknown>;
    initialBounds: { x: number; y: number; width: number; height: number };
  } | null>(null);

  // Drag state (refs — no re-renders needed)
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const isDrawingRef = useRef(false);
  const [, forceRender] = useState(0); // only used to update cursor reactively

  const runtimeQuery = usePdfRuntimeQuery();
  const documentQuery = usePdfDocumentQuery(fileUrl);
  const lastAppliedSearchFocusNonceRef = useRef<number | null>(null);

  const getEffectiveLayerData = useCallback((layer: Layer) => {
    return draftLayerData[layer.id] ?? layer.data;
  }, [draftLayerData]);

  const isLayerSelected = useCallback(
    (layerId: string) => selectedLayerIds.includes(layerId),
    [selectedLayerIds]
  );

  const getLayerBounds = useCallback((layerData: Record<string, unknown>, kind: LayerKind) => {
    if (kind === 'line') {
      const x1 = Number(layerData.x1 ?? 0);
      const y1 = Number(layerData.y1 ?? 0);
      const x2 = Number(layerData.x2 ?? 0);
      const y2 = Number(layerData.y2 ?? 0);
      return {
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.max(8, Math.abs(x2 - x1)),
        height: Math.max(8, Math.abs(y2 - y1)),
      };
    }
    return {
      x: Number(layerData.x ?? 0),
      y: Number(layerData.y ?? 0),
      width: Math.max(8, Number(layerData.width ?? 80)),
      height: Math.max(8, Number(layerData.height ?? 40)),
    };
  }, []);

  const snap = useCallback((value: number) => {
    return Math.round(value * 2) / 2;
  }, []);

  const snapRect = useCallback((x: number, y: number, width: number, height: number) => {
    const sx = snap(x);
    const sy = snap(y);
    const sw = Math.max(1, snap(width));
    const sh = Math.max(1, snap(height));
    return { x: sx, y: sy, width: sw, height: sh };
  }, [snap]);

  const expandBounds = useCallback((bounds: { x: number; y: number; width: number; height: number }) => {
    return {
      x: bounds.x - SELECTION_PADDING,
      y: bounds.y - SELECTION_PADDING,
      width: bounds.width + SELECTION_PADDING * 2,
      height: bounds.height + SELECTION_PADDING * 2,
    };
  }, []);

  const getLayerKind = useCallback((layer: Layer, layerData: Record<string, unknown>): LayerKind => {
    if (layer.layer_type === 'measurement') return 'line';
    if (layer.layer_type === 'shape' && String(layerData.shapeType ?? 'cloud') === 'arrow') return 'line';
    return 'box';
  }, []);

  const selectedContextLayer = selectedLayerIds.length
    ? layers.find((layer) => layer.id === selectedLayerIds[selectedLayerIds.length - 1]) ?? null
    : null;
  const selectedContextLayerData = selectedContextLayer ? getEffectiveLayerData(selectedContextLayer) : null;
  const selectedContextIsText = Boolean(
    selectedContextLayer
      && (selectedContextLayer.layer_type === 'text' || selectedContextLayer.layer_type === 'annotation')
  );
  const selectedContextFrame = selectedContextLayer && selectedContextLayerData
    ? expandBounds(getLayerBounds(selectedContextLayerData, getLayerKind(selectedContextLayer, selectedContextLayerData)))
    : null;
  const selectedContextMenuPosition = selectedContextFrame
    ? (() => {
      const rawLeft =
        viewerState.position.x
        + (selectedContextFrame.x + selectedContextFrame.width / 2) * viewerState.scale;
      const rawTop = viewerState.position.y + selectedContextFrame.y * viewerState.scale - 8;
      const containerWidth = containerRef.current?.clientWidth ?? 0;
      const halfToolbarWidth = selectedContextIsText ? 96 : 48;

      return {
        left: containerWidth > 0
          ? Math.min(
            Math.max(rawLeft, halfToolbarWidth + 8),
            Math.max(halfToolbarWidth + 8, containerWidth - halfToolbarWidth - 8),
          )
          : rawLeft,
        top: Math.max(rawTop, 44),
      };
    })()
    : null;
  const selectedTextBox = selectedContextIsText && selectedContextLayerData
    ? {
      x: Number(selectedContextLayerData.x ?? 20),
      y: Number(selectedContextLayerData.y ?? 20),
      width: Math.max(60, Number(selectedContextLayerData.width ?? 100)),
      height: Math.max(24, Number(selectedContextLayerData.height ?? 32)),
    }
    : null;

  useEffect(() => {
    if (!selectedContextIsText || !selectedContextLayer || !selectedTextBox) return;
    if (lastAutofocusLayerIdRef.current === selectedContextLayer.id) return;
    lastAutofocusLayerIdRef.current = selectedContextLayer.id;

    const hasCoarsePointer = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    const isTouchCapable = typeof window !== 'undefined'
      && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    if (!hasCoarsePointer && !isTouchCapable) return;

    const rafId = requestAnimationFrame(() => {
      const editor = textEditorRef.current;
      if (!editor) return;
      editor.focus({ preventScroll: true });
      const end = editor.value.length;
      editor.setSelectionRange(end, end);
    });

    return () => cancelAnimationFrame(rafId);
  }, [selectedContextIsText, selectedContextLayer, selectedTextBox]);
  const observationCallouts = useMemo(() => (
    layers
      .filter((layer) => layer.visible && Boolean(layer.observation))
      .map((layer) => {
        const layerData = getEffectiveLayerData(layer);
        const kind = getLayerKind(layer, layerData);
        const bounds = getLayerBounds(layerData, kind);
        const observation = layer.observation ? observationLookup[layer.observation] : undefined;
        const labelText = String(layerData.observation_label ?? (observation?.number !== undefined ? `OBS-${observation.number}` : 'OBS'));
        const labelWidth = Math.max(56, Math.min(120, labelText.length * 8 + 18));
        const labelHeight = 24;
        const labelX = bounds.x + bounds.width + 18;
        const labelY = bounds.y - 6;
        const lineStartX = labelX;
        const lineStartY = labelY + labelHeight / 2;
        const lineEndX = bounds.x + bounds.width;
        const lineEndY = Math.max(bounds.y + 6, Math.min(bounds.y + bounds.height - 6, lineStartY));
        return {
          layerId: layer.id,
          markerId: `obs-arrowhead-${layer.id}`,
          labelText,
          labelX,
          labelY,
          labelWidth,
          labelHeight,
          lineStartX,
          lineStartY,
          lineEndX,
          lineEndY,
          observation,
        };
      })
  ), [getEffectiveLayerData, getLayerBounds, getLayerKind, layers, observationLookup]);
  const punchCallouts = useMemo(() => (
    layers
      .filter((layer) => layer.visible && Boolean(layer.punch_list_item))
      .map((layer) => {
        const layerData = getEffectiveLayerData(layer);
        const kind = getLayerKind(layer, layerData);
        const bounds = getLayerBounds(layerData, kind);
        const punchItem = layer.punch_list_item ? punchLookup[layer.punch_list_item] : undefined;
        const baseLabel = typeof layerData.punch_label === 'string' ? layerData.punch_label : '';
        const labelText = baseLabel || (punchItem?.title ? `PUNCH: ${punchItem.title}` : 'PUNCH');
        const labelWidth = Math.max(72, Math.min(220, labelText.length * 7 + 20));
        const labelHeight = 24;
        const labelX = bounds.x + bounds.width + 18;
        const labelY = bounds.y + bounds.height + 8;
        const lineStartX = labelX;
        const lineStartY = labelY + labelHeight / 2;
        const lineEndX = bounds.x + bounds.width;
        const lineEndY = Math.max(bounds.y + 6, Math.min(bounds.y + bounds.height - 6, lineStartY));
        return {
          layerId: layer.id,
          markerId: `punch-arrowhead-${layer.id}`,
          labelText,
          labelX,
          labelY,
          labelWidth,
          labelHeight,
          lineStartX,
          lineStartY,
          lineEndX,
          lineEndY,
          punchItem,
        };
      })
  ), [getEffectiveLayerData, getLayerBounds, getLayerKind, layers, punchLookup]);
  const submittalCallouts = useMemo(() => (
    layers
      .filter((layer) => layer.visible && Boolean(layer.submittal))
      .map((layer) => {
        const layerData = getEffectiveLayerData(layer);
        const kind = getLayerKind(layer, layerData);
        const bounds = getLayerBounds(layerData, kind);
        const submittal = layer.submittal ? submittalLookup[layer.submittal] : undefined;
        const baseLabel = typeof layerData.submittal_label === 'string' ? layerData.submittal_label : '';
        const labelText = baseLabel || submittal?.submittal_number || 'SUBMITTAL';
        const labelWidth = Math.max(84, Math.min(220, labelText.length * 7 + 20));
        const labelHeight = 24;
        const labelX = bounds.x + bounds.width + 18;
        const labelY = bounds.y - 34;
        const lineStartX = labelX;
        const lineStartY = labelY + labelHeight / 2;
        const lineEndX = bounds.x + bounds.width;
        const lineEndY = Math.max(bounds.y + 6, Math.min(bounds.y + bounds.height - 6, lineStartY));
        return {
          layerId: layer.id,
          markerId: `submittal-arrowhead-${layer.id}`,
          labelText,
          labelX,
          labelY,
          labelWidth,
          labelHeight,
          lineStartX,
          lineStartY,
          lineEndX,
          lineEndY,
          submittal,
        };
      })
  ), [getEffectiveLayerData, getLayerBounds, getLayerKind, layers, submittalLookup]);
  const rfiCallouts = useMemo(() => (
    layers
      .filter((layer) => layer.visible && Boolean(layer.rfi))
      .map((layer) => {
        const layerData = getEffectiveLayerData(layer);
        const kind = getLayerKind(layer, layerData);
        const bounds = getLayerBounds(layerData, kind);
        const rfi = layer.rfi ? rfiLookup[layer.rfi] : undefined;
        const baseLabel = typeof layerData.rfi_label === 'string' ? layerData.rfi_label : '';
        const labelText = baseLabel || rfi?.rfi_number || 'RFI';
        const labelWidth = Math.max(72, Math.min(220, labelText.length * 7 + 20));
        const labelHeight = 24;
        const labelX = bounds.x + bounds.width + 18;
        const labelY = bounds.y - 62;
        const lineStartX = labelX;
        const lineStartY = labelY + labelHeight / 2;
        const lineEndX = bounds.x + bounds.width;
        const lineEndY = Math.max(bounds.y + 6, Math.min(bounds.y + bounds.height - 6, lineStartY));
        return {
          layerId: layer.id,
          markerId: `rfi-arrowhead-${layer.id}`,
          labelText,
          labelX,
          labelY,
          labelWidth,
          labelHeight,
          lineStartX,
          lineStartY,
          lineEndX,
          lineEndY,
          rfi,
        };
      })
  ), [getEffectiveLayerData, getLayerBounds, getLayerKind, layers, rfiLookup]);

  const startMoveInteraction = useCallback((
    e: {
      clientX: number;
      clientY: number;
      metaKey?: boolean;
      ctrlKey?: boolean;
      stopPropagation: () => void;
    },
    layer: Layer,
    layerData: Record<string, unknown>,
    kind: LayerKind
  ) => {
    e.stopPropagation();
    const isToggle = e.metaKey || e.ctrlKey;
    if (isToggle) {
      onLayerClick?.(layer.id, { toggle: true });
      return;
    }

    const moveLayerIds = isLayerSelected(layer.id)
      ? [...selectedLayerIds]
      : [layer.id];

    if (!isLayerSelected(layer.id)) {
      onLayerClick?.(layer.id, { exclusive: true });
    }

    const initialDataById: Record<string, Record<string, unknown>> = {};
    for (const moveId of moveLayerIds) {
      const moveLayer = layers.find((candidate) => candidate.id === moveId);
      if (!moveLayer) continue;
      initialDataById[moveId] = getEffectiveLayerData(moveLayer);
    }

    setInteractionState({
      layerId: layer.id,
      mode: 'move',
      kind,
      moveLayerIds,
      initialDataById,
      startClient: { x: e.clientX, y: e.clientY },
      initialData: layerData,
      initialBounds: getLayerBounds(layerData, kind),
    });
  }, [
    getEffectiveLayerData,
    getLayerBounds,
    isLayerSelected,
    layers,
    onLayerClick,
    selectedLayerIds,
  ]);

  const startResizeInteraction = useCallback((
    e: {
      clientX: number;
      clientY: number;
      stopPropagation: () => void;
    },
    layerId: string,
    layerData: Record<string, unknown>,
    corner: ResizeCorner,
    kind: LayerKind
  ) => {
    e.stopPropagation();
    setInteractionState({
      layerId,
      mode: 'resize-corner',
      corner,
      kind,
      startClient: { x: e.clientX, y: e.clientY },
      initialData: layerData,
      initialBounds: getLayerBounds(layerData, kind),
    });
  }, [getLayerBounds]);

  // Notify parent when page count is known.
  useEffect(() => {
    if (documentQuery.data) onLoadSuccess(documentQuery.data.numPages);
  }, [documentQuery.data, onLoadSuccess]);

  // Zoom level the user has held still at for RENDER_SETTLE_MS. Drives
  // the re-rasterisation below without thrashing during wheel/pinch.
  const [settledScale, setSettledScale] = useState(viewerState.scale);
  useEffect(() => {
    const id = window.setTimeout(() => setSettledScale(viewerState.scale), RENDER_SETTLE_MS);
    return () => window.clearTimeout(id);
  }, [viewerState.scale]);

  // What the canvas currently holds, so zoom-driven re-runs can tell a
  // content change (blank + show loader) from a resolution-only change
  // (repaint in place) — and skip entirely when the budget clamps to
  // the scale we already rendered.
  const lastRenderedRef = useRef<{
    doc: unknown;
    page: number;
    rotation: number;
    cropKey: string;
    renderScale: number;
  } | null>(null);

  // Render PDF page to canvas whenever the document/page/rotation
  // changes, or when the zoom settles at a scale that needs a sharper
  // (or smaller) raster than the canvas currently holds.
  useEffect(() => {
    const pdfDoc = documentQuery.data;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    const cropKey = cropRegion ? cropRegion.join(',') : '';
    const last = lastRenderedRef.current;
    const contentChanged =
      !last ||
      last.doc !== pdfDoc ||
      last.page !== viewerState.currentPage ||
      last.rotation !== viewerState.rotation ||
      last.cropKey !== cropKey;

    let cancelled = false;
    let activePage: PdfPageProxyWithCleanup | null = null;
    let firstReadyFrame: number | null = null;
    let secondReadyFrame: number | null = null;

    // Free the *previous* content's pixel buffer before we allocate the
    // new one. Without this, the browser briefly holds two large bitmaps
    // at once, and on iOS WKWebView that's enough to OOM-kill the page on
    // the second layout switch. Skipped for zoom-only re-renders so the
    // stale (blurry) raster stays visible until the crisp one paints.
    if (contentChanged && (canvas.width > 1 || canvas.height > 1)) {
      canvas.width = 0;
      canvas.height = 0;
    }

    const render = async () => {
      try {
        setRenderError(null);
        // Only content changes show the blocking loader; zoom re-renders
        // paint over the existing raster without hiding the viewer.
        if (contentChanged) setIsRendering(true);

        const page = (await pdfDoc.getPage(viewerState.currentPage)) as PdfPageProxyWithCleanup;
        if (cancelled || !canvasRef.current) {
          page.cleanup?.();
          return;
        }
        activePage = page;

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas 2d context');

        // Natural (scale-1) dimensions — use crop region if set, otherwise full page.
        const naturalViewport = page.getViewport({ scale: 1, rotation: viewerState.rotation });
        const dimensions = cropRegion
          ? { width: cropRegion[2], height: cropRegion[3] }
          : { width: naturalViewport.width, height: naturalViewport.height };
        if (contentChanged) {
          setPdfDimensions(dimensions);
          onDimensionsLoad?.(dimensions);
        }

        // Pick a render scale that's crisp on capable devices but safe
        // for iOS Safari's canvas-area cap on large drawings. Use the
        // *full page* size as the budget input even when cropping,
        // because PDF.js still rasterises the whole page into the
        // canvas — we just translate to show only the crop region.
        // Once the zoom settles above the default, chase the on-screen
        // resolution (display scale × devicePixelRatio); the area and
        // dimension budgets still clamp it to device-safe sizes.
        const devicePixelRatio = window.devicePixelRatio || 1;
        const renderScale = pickSafeRenderScale(
          naturalViewport.width,
          naturalViewport.height,
          Math.max(DESIRED_RENDER_SCALE, settledScale * devicePixelRatio),
          { ignoreScaleCap: true },
        );

        // Zoom settled, but the budget clamped to the scale already on
        // the canvas — it's as crisp as it can get, keep it.
        if (!contentChanged && last && last.renderScale === renderScale) {
          page.cleanup?.();
          return;
        }

        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const renderViewport = page.getViewport({ scale: renderScale, rotation: viewerState.rotation });
        canvasRef.current.width = Math.round(dimensions.width * renderScale);
        canvasRef.current.height = Math.round(dimensions.height * renderScale);
        canvasRef.current.style.width = `${dimensions.width}px`;
        canvasRef.current.style.height = `${dimensions.height}px`;

        if (cropRegion) {
          // Shift the render context so the crop origin aligns with (0,0)
          ctx.translate(-cropRegion[0] * renderScale, -cropRegion[1] * renderScale);
        }

        const task = page.render({ canvasContext: ctx, viewport: renderViewport });
        renderTaskRef.current = task;
        await task.promise;

        if (!cancelled && renderTaskRef.current === task) {
          renderTaskRef.current = null;
          setIsRendering(false);
          lastRenderedRef.current = {
            doc: pdfDoc,
            page: viewerState.currentPage,
            rotation: viewerState.rotation,
            cropKey,
            renderScale,
          };

          if (contentChanged) {
            firstReadyFrame = window.requestAnimationFrame(() => {
              secondReadyFrame = window.requestAnimationFrame(() => {
                if (!cancelled) onInitialRenderComplete?.();
              });
            });
          }
        }

        // Drop the page's operator list / image cache now that we've
        // rasterised it — keeps ~5–20 MB per page off the heap.
        page.cleanup?.();
      } catch (err) {
        const typed = err as { name?: string; message?: string };
        if (typed.name === 'RenderingCancelledException') return;
        if (!cancelled) {
          setRenderError(typed.message ?? 'Failed to render PDF page.');
          setIsRendering(false);
          onInitialRenderError?.();
        }
      }
    };

    render();
    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      activePage?.cleanup?.();
      if (firstReadyFrame !== null) window.cancelAnimationFrame(firstReadyFrame);
      if (secondReadyFrame !== null) window.cancelAnimationFrame(secondReadyFrame);
    };
  }, [
    documentQuery.data,
    viewerState.currentPage,
    viewerState.rotation,
    cropRegion,
    settledScale,
    onDimensionsLoad,
    onInitialRenderComplete,
    onInitialRenderError,
  ]);

  useEffect(() => {
    if (runtimeQuery.isError || documentQuery.isError) {
      onInitialRenderError?.();
    }
  }, [runtimeQuery.isError, documentQuery.isError, onInitialRenderError]);

  // Release the canvas backing store on unmount so a stale ~70 MB
  // pixel buffer doesn't survive a component unmount/remount cycle.
  // (Done here, not in the render effect's cleanup, so zoom-driven
  // re-runs don't blank the canvas between renders.)
  useEffect(() => () => {
    const c = canvasRef.current;
    if (c) {
      c.width = 0;
      c.height = 0;
    }
  }, []);

  // ── Auto-fit: compute scale and centered position once per file load ──
  const hasFitRef = useRef(false);
  useEffect(() => {
    hasFitRef.current = false;
  }, [fileUrl, cropRegion]);

  useEffect(() => {
    if (!pdfDimensions || hasFitRef.current || !containerRef.current) return;

    const containerW = containerRef.current.clientWidth;
    const containerH = containerRef.current.clientHeight;
    if (!containerW || !containerH) return;

    const PADDING = 32; // px breathing room on each side
    const fitScale = Math.min(
      (containerW - PADDING * 2) / pdfDimensions.width,
      (containerH - PADDING * 2) / pdfDimensions.height,
    );

    // Center the scaled PDF within the container.
    const scaledW = pdfDimensions.width * fitScale;
    const scaledH = pdfDimensions.height * fitScale;
    const centeredX = (containerW - scaledW) / 2;
    const centeredY = (containerH - scaledH) / 2;

    hasFitRef.current = true;
    onFitComplete?.(fitScale, { x: centeredX, y: centeredY });
    onScaleChange(fitScale);
    onPositionChange({ x: centeredX, y: centeredY });
  }, [pdfDimensions, fileUrl, onFitComplete, onScaleChange, onPositionChange]);

  // ── Wheel zoom: zoom toward cursor, updating both scale and position ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const zoomSpeed = 0.1;
      const factor = e.deltaY > 0 ? 1 - zoomSpeed : 1 + zoomSpeed;
      const newScale = Math.max(0.05, Math.min(20, viewerState.scale * factor));
      if (newScale === viewerState.scale) return;

      // Mouse position relative to the container element.
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Point under cursor in PDF coordinates (before zoom).
      const pdfX = (mouseX - viewerState.position.x) / viewerState.scale;
      const pdfY = (mouseY - viewerState.position.y) / viewerState.scale;

      // New position so that same PDF point stays under cursor after zoom.
      const newX = mouseX - pdfX * newScale;
      const newY = mouseY - pdfY * newScale;

      onPositionChange({ x: newX, y: newY });
      onScaleChange(newScale);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [viewerState.scale, viewerState.position, onScaleChange, onPositionChange]);

  // ── Search focus zoom: triggered by Enter from toolbar search input ──
  useEffect(() => {
    if (searchFocusRequestNonce === undefined || searchFocusRequestNonce === null) return;
    if (lastAppliedSearchFocusNonceRef.current === searchFocusRequestNonce) return;
    if (!containerRef.current || !pdfDimensions) return;

    const activeHighlight = searchHighlights.find((highlight) => highlight.active);
    if (!activeHighlight || !activeHighlight.points?.length) return;

    const xs = activeHighlight.points.map((p) => p.x);
    const ys = activeHighlight.points.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const bboxWidth = Math.max(14, maxX - minX);
    const bboxHeight = Math.max(10, maxY - minY);
    const centerX = minX + bboxWidth / 2;
    const centerY = minY + bboxHeight / 2;

    const containerW = containerRef.current.clientWidth;
    const containerH = containerRef.current.clientHeight;
    if (!containerW || !containerH) return;

    const fitByWidth = (containerW - 120) / bboxWidth;
    const fitByHeight = (containerH - 100) / bboxHeight;
    const desiredScale = Math.min(8, Math.max(1.2, Math.min(fitByWidth, fitByHeight)));
    const nextScale = Math.max(viewerState.scale, desiredScale);

    const nextPosition = {
      x: containerW / 2 - centerX * nextScale,
      y: containerH / 2 - centerY * nextScale,
    };

    lastAppliedSearchFocusNonceRef.current = searchFocusRequestNonce;
    onScaleChange(nextScale);
    onPositionChange(nextPosition);
  }, [
    onPositionChange,
    onScaleChange,
    pdfDimensions,
    searchFocusRequestNonce,
    searchHighlights,
    viewerState.scale,
  ]);

  // ── Drag to pan ──
  // Track mousedown position to distinguish a click from a drag.
  const mouseDownClientRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();

    if (drawingEnabled) {
      const point = clientToPdfPoint(e.clientX, e.clientY);
      if (!point) return;
      isDrawingRef.current = true;
      setDrawStartPoint(point);
      setDrawCurrentPoint(point);
      forceRender(n => n + 1);
      return;
    }

    isDraggingRef.current = false;
    mouseDownClientRef.current = { x: e.clientX, y: e.clientY };
    dragStartRef.current = {
      x: e.clientX - viewerState.position.x,
      y: e.clientY - viewerState.position.y,
    };
    forceRender(n => n + 1);
  }, [viewerState.position, drawingEnabled, clientToPdfPoint]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (interactionState) {
        const dx = (e.clientX - interactionState.startClient.x) / viewerState.scale;
        const dy = (e.clientY - interactionState.startClient.y) / viewerState.scale;
        const initial = interactionState.initialData;
        const nextData: Record<string, unknown> = { ...initial };

        if (interactionState.mode === 'move') {
          const moveIds = interactionState.moveLayerIds ?? [interactionState.layerId];
          const initialById = interactionState.initialDataById ?? { [interactionState.layerId]: initial };
          const updates: Record<string, Record<string, unknown>> = {};

          for (const moveId of moveIds) {
            const moveInitial = initialById[moveId];
            if (!moveInitial) continue;
            const candidate: Record<string, unknown> = { ...moveInitial };
            if (typeof moveInitial.x1 === 'number' && typeof moveInitial.y1 === 'number' && typeof moveInitial.x2 === 'number' && typeof moveInitial.y2 === 'number') {
              candidate.x1 = Number(moveInitial.x1) + dx;
              candidate.y1 = Number(moveInitial.y1) + dy;
              candidate.x2 = Number(moveInitial.x2) + dx;
              candidate.y2 = Number(moveInitial.y2) + dy;
            } else {
              candidate.x = Number(moveInitial.x ?? 0) + dx;
              candidate.y = Number(moveInitial.y ?? 0) + dy;
            }
            updates[moveId] = candidate;
          }

          setDraftLayerData((prev) => ({ ...prev, ...updates }));
          return;
        } else if (interactionState.mode === 'resize-corner' && interactionState.corner) {
          const b = interactionState.initialBounds;
          let left = b.x;
          let top = b.y;
          let right = b.x + b.width;
          let bottom = b.y + b.height;

          if (interactionState.corner === 'tl') {
            left = b.x + dx;
            top = b.y + dy;
          } else if (interactionState.corner === 'tr') {
            right = b.x + b.width + dx;
            top = b.y + dy;
          } else if (interactionState.corner === 'br') {
            right = b.x + b.width + dx;
            bottom = b.y + b.height + dy;
          } else if (interactionState.corner === 'bl') {
            left = b.x + dx;
            bottom = b.y + b.height + dy;
          }

          const minSize = 8;
          if (right - left < minSize) {
            if (interactionState.corner === 'tl' || interactionState.corner === 'bl') left = right - minSize;
            else right = left + minSize;
          }
          if (bottom - top < minSize) {
            if (interactionState.corner === 'tl' || interactionState.corner === 'tr') top = bottom - minSize;
            else bottom = top + minSize;
          }

          let newW = right - left;
          let newH = bottom - top;

          const isPinShape = String(initial.shapeType ?? '') === 'pin';
          if (interactionState.kind === 'box' && isPinShape) {
            const size = Math.max(8, Math.max(newW, newH));
            if (interactionState.corner === 'tl') {
              left = right - size;
              top = bottom - size;
            } else if (interactionState.corner === 'tr') {
              right = left + size;
              top = bottom - size;
            } else if (interactionState.corner === 'br') {
              right = left + size;
              bottom = top + size;
            } else if (interactionState.corner === 'bl') {
              left = right - size;
              bottom = top + size;
            }
            newW = right - left;
            newH = bottom - top;
          }

          if (interactionState.kind === 'box') {
            nextData.x = left;
            nextData.y = top;
            nextData.width = newW;
            nextData.height = newH;
          } else {
            const srcLeft = b.x;
            const srcTop = b.y;
            const srcW = Math.max(1, b.width);
            const srcH = Math.max(1, b.height);
            const sx = newW / srcW;
            const sy = newH / srcH;
            nextData.x1 = left + (Number(initial.x1 ?? 0) - srcLeft) * sx;
            nextData.y1 = top + (Number(initial.y1 ?? 0) - srcTop) * sy;
            nextData.x2 = left + (Number(initial.x2 ?? 0) - srcLeft) * sx;
            nextData.y2 = top + (Number(initial.y2 ?? 0) - srcTop) * sy;
          }
        }

        setDraftLayerData((prev) => ({
          ...prev,
          [interactionState.layerId]: nextData,
        }));
        return;
      }

      if (isDrawingRef.current) {
        const point = clientToPdfPoint(e.clientX, e.clientY);
        if (point) setDrawCurrentPoint(point);
        return;
      }
      if (!dragStartRef.current) return;
      isDraggingRef.current = true;
      onPositionChange({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (interactionState) {
        const moveIds = interactionState.moveLayerIds ?? [interactionState.layerId];
        const initialById = interactionState.initialDataById ?? { [interactionState.layerId]: interactionState.initialData };
        const updates = moveIds.map((layerId) => ({
          layerId,
          nextData: draftLayerData[layerId] ?? initialById[layerId],
        })).filter((entry) => Boolean(entry.nextData));

        setDraftLayerData((prev) => {
          const next = { ...prev };
          for (const moveId of moveIds) {
            delete next[moveId];
          }
          return next;
        });
        setInteractionState(null);
        if (onLayerChange) {
          for (const update of updates) {
            onLayerChange(update.layerId, update.nextData as Record<string, unknown>);
          }
        }
        return;
      }

      if (isDrawingRef.current) {
        const point = clientToPdfPoint(e.clientX, e.clientY);
        const start = drawStartPoint;
        isDrawingRef.current = false;
        setDrawCurrentPoint(null);
        setDrawStartPoint(null);
        forceRender(n => n + 1);
        if (start && point && onShapeDrawComplete) {
          onShapeDrawComplete(start, point);
        }
        return;
      }
      if (!dragStartRef.current) return;
      const wasDrag = isDraggingRef.current;
      const downClient = mouseDownClientRef.current;
      dragStartRef.current = null;
      isDraggingRef.current = false;
      mouseDownClientRef.current = null;
      forceRender(n => n + 1);
      // Fire onBackgroundClick only for a true click (no movement).
      if (!wasDrag && downClient && onBackgroundClick) {
        const point = clientToPdfPoint(e.clientX, e.clientY);
        if (point) onBackgroundClick(point.x, point.y);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    onPositionChange,
    onBackgroundClick,
    clientToPdfPoint,
    drawStartPoint,
    onShapeDrawComplete,
    interactionState,
    viewerState.scale,
    draftLayerData,
    onLayerChange,
  ]);

  // ── Touch: one-finger pan, two-finger pinch zoom ──
  const touchStartDistanceRef = useRef<number | null>(null);
  const touchStartScaleRef = useRef<number>(1);
  const gestureBecamePinchRef = useRef(false);
  const currentScaleRef = useRef(viewerState.scale);
  const currentPositionRef = useRef(viewerState.position);
  const pinchAnchorRef = useRef<{ pdfX: number; pdfY: number } | null>(null);
  const touchGestureActiveRef = useRef(false);

  useEffect(() => {
    currentScaleRef.current = viewerState.scale;
    currentPositionRef.current = viewerState.position;
  }, [viewerState.scale, viewerState.position]);

  useEffect(() => {
    let touchDragged = false;
    let touchStartClient: { x: number; y: number } | null = null;

    const getContainerPoint = (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return null;
      const rect = container.getBoundingClientRect();
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const isTouchWithinContainer = (touch: Touch) => {
      const container = containerRef.current;
      if (!container) return false;
      const rect = container.getBoundingClientRect();
      return (
        touch.clientX >= rect.left &&
        touch.clientX <= rect.right &&
        touch.clientY >= rect.top &&
        touch.clientY <= rect.bottom
      );
    };

    const isEventTargetInsideContainer = (target: EventTarget | null) => {
      const container = containerRef.current;
      if (!container) return false;
      return target instanceof Node && container.contains(target);
    };

    const handleTouchStart = (e: TouchEvent) => {
      const anyTouchInside = Array.from(e.touches).some(isTouchWithinContainer);
      const targetInsideContainer = isEventTargetInsideContainer(e.target);
      if (!anyTouchInside || !targetInsideContainer) {
        touchGestureActiveRef.current = false;
        return;
      }
      touchGestureActiveRef.current = true;

      touchDragged = false;
      gestureBecamePinchRef.current = false;
      if (interactionState) {
        return;
      }
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        if (drawingEnabled) {
          e.preventDefault();
          const point = clientToPdfPoint(touch.clientX, touch.clientY);
          if (!point) return;
          isDrawingRef.current = true;
          setDrawStartPoint(point);
          setDrawCurrentPoint(point);
          dragStartRef.current = null;
          touchStartClient = null;
          forceRender((n) => n + 1);
          return;
        }
        touchStartClient = { x: touch.clientX, y: touch.clientY };
        const livePosition = currentPositionRef.current;
        dragStartRef.current = {
          x: touch.clientX - livePosition.x,
          y: touch.clientY - livePosition.y,
        };
      } else if (e.touches.length === 2) {
        e.preventDefault();
        gestureBecamePinchRef.current = true;
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        touchStartDistanceRef.current = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        touchStartScaleRef.current = currentScaleRef.current;
        const midpoint = getContainerPoint((t1.clientX + t2.clientX) / 2, (t1.clientY + t2.clientY) / 2);
        const livePosition = currentPositionRef.current;
        const liveScale = currentScaleRef.current;
        if (midpoint && liveScale > 0) {
          pinchAnchorRef.current = {
            pdfX: (midpoint.x - livePosition.x) / liveScale,
            pdfY: (midpoint.y - livePosition.y) / liveScale,
          };
        } else {
          pinchAnchorRef.current = null;
        }
        dragStartRef.current = null;
        touchStartClient = null;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchGestureActiveRef.current) return;
      if (e.touches.length === 1 && interactionState) {
        touchDragged = true;
        e.preventDefault();
        const touch = e.touches[0];
        const dx = (touch.clientX - interactionState.startClient.x) / viewerState.scale;
        const dy = (touch.clientY - interactionState.startClient.y) / viewerState.scale;
        const initial = interactionState.initialData;
        const nextData: Record<string, unknown> = { ...initial };

        if (interactionState.mode === 'move') {
          const moveIds = interactionState.moveLayerIds ?? [interactionState.layerId];
          const initialById = interactionState.initialDataById ?? { [interactionState.layerId]: initial };
          const updates: Record<string, Record<string, unknown>> = {};

          for (const moveId of moveIds) {
            const moveInitial = initialById[moveId];
            if (!moveInitial) continue;
            const candidate: Record<string, unknown> = { ...moveInitial };
            if (
              typeof moveInitial.x1 === 'number' &&
              typeof moveInitial.y1 === 'number' &&
              typeof moveInitial.x2 === 'number' &&
              typeof moveInitial.y2 === 'number'
            ) {
              candidate.x1 = Number(moveInitial.x1) + dx;
              candidate.y1 = Number(moveInitial.y1) + dy;
              candidate.x2 = Number(moveInitial.x2) + dx;
              candidate.y2 = Number(moveInitial.y2) + dy;
            } else {
              candidate.x = Number(moveInitial.x ?? 0) + dx;
              candidate.y = Number(moveInitial.y ?? 0) + dy;
            }
            updates[moveId] = candidate;
          }

          setDraftLayerData((prev) => ({ ...prev, ...updates }));
          return;
        }

        if (interactionState.mode === 'resize-corner' && interactionState.corner) {
          const b = interactionState.initialBounds;
          let left = b.x;
          let top = b.y;
          let right = b.x + b.width;
          let bottom = b.y + b.height;

          if (interactionState.corner === 'tl') {
            left = b.x + dx;
            top = b.y + dy;
          } else if (interactionState.corner === 'tr') {
            right = b.x + b.width + dx;
            top = b.y + dy;
          } else if (interactionState.corner === 'br') {
            right = b.x + b.width + dx;
            bottom = b.y + b.height + dy;
          } else if (interactionState.corner === 'bl') {
            left = b.x + dx;
            bottom = b.y + b.height + dy;
          }

          const minSize = 8;
          if (right - left < minSize) {
            if (interactionState.corner === 'tl' || interactionState.corner === 'bl') left = right - minSize;
            else right = left + minSize;
          }
          if (bottom - top < minSize) {
            if (interactionState.corner === 'tl' || interactionState.corner === 'tr') top = bottom - minSize;
            else bottom = top + minSize;
          }

          let newW = right - left;
          let newH = bottom - top;

          const isPinShape = String(initial.shapeType ?? '') === 'pin';
          if (interactionState.kind === 'box' && isPinShape) {
            const size = Math.max(8, Math.max(newW, newH));
            if (interactionState.corner === 'tl') {
              left = right - size;
              top = bottom - size;
            } else if (interactionState.corner === 'tr') {
              right = left + size;
              top = bottom - size;
            } else if (interactionState.corner === 'br') {
              right = left + size;
              bottom = top + size;
            } else if (interactionState.corner === 'bl') {
              left = right - size;
              bottom = top + size;
            }
            newW = right - left;
            newH = bottom - top;
          }

          if (interactionState.kind === 'box') {
            nextData.x = left;
            nextData.y = top;
            nextData.width = newW;
            nextData.height = newH;
          } else {
            const srcLeft = b.x;
            const srcTop = b.y;
            const srcW = Math.max(1, b.width);
            const srcH = Math.max(1, b.height);
            const sx = newW / srcW;
            const sy = newH / srcH;
            nextData.x1 = left + (Number(initial.x1 ?? 0) - srcLeft) * sx;
            nextData.y1 = top + (Number(initial.y1 ?? 0) - srcTop) * sy;
            nextData.x2 = left + (Number(initial.x2 ?? 0) - srcLeft) * sx;
            nextData.y2 = top + (Number(initial.y2 ?? 0) - srcTop) * sy;
          }
        }

        setDraftLayerData((prev) => ({
          ...prev,
          [interactionState.layerId]: nextData,
        }));
      } else if (e.touches.length === 1 && isDrawingRef.current) {
        touchDragged = true;
        e.preventDefault();
        const touch = e.touches[0];
        const point = clientToPdfPoint(touch.clientX, touch.clientY);
        if (point) setDrawCurrentPoint(point);
      } else if (e.touches.length === 1 && dragStartRef.current) {
        touchDragged = true;
        e.preventDefault();
        const touch = e.touches[0];
        onPositionChange({
          x: touch.clientX - dragStartRef.current.x,
          y: touch.clientY - dragStartRef.current.y,
        });
      } else if (e.touches.length === 2 && touchStartDistanceRef.current !== null) {
        touchDragged = true;
        e.preventDefault();
        gestureBecamePinchRef.current = true;
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const newScale = Math.max(0.05, Math.min(20, touchStartScaleRef.current * (dist / touchStartDistanceRef.current)));
        const midpoint = getContainerPoint((t1.clientX + t2.clientX) / 2, (t1.clientY + t2.clientY) / 2);
        const anchor = pinchAnchorRef.current;
        if (midpoint && anchor) {
          onPositionChange({
            x: midpoint.x - anchor.pdfX * newScale,
            y: midpoint.y - anchor.pdfY * newScale,
          });
        }
        onScaleChange(newScale);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchGestureActiveRef.current) return;
      if (interactionState) {
        const moveIds = interactionState.moveLayerIds ?? [interactionState.layerId];
        const initialById = interactionState.initialDataById ?? { [interactionState.layerId]: interactionState.initialData };
        const updates = moveIds.map((layerId) => ({
          layerId,
          nextData: draftLayerData[layerId] ?? initialById[layerId],
        })).filter((entry) => Boolean(entry.nextData));

        setDraftLayerData((prev) => {
          const next = { ...prev };
          for (const moveId of moveIds) {
            delete next[moveId];
          }
          return next;
        });
        setInteractionState(null);
        if (onLayerChange) {
          for (const update of updates) {
            onLayerChange(update.layerId, update.nextData as Record<string, unknown>);
          }
        }
        return;
      }

      if (isDrawingRef.current) {
        const changed = e.changedTouches[0];
        const point = changed ? clientToPdfPoint(changed.clientX, changed.clientY) : null;
        const start = drawStartPoint;
        isDrawingRef.current = false;
        setDrawCurrentPoint(null);
        setDrawStartPoint(null);
        forceRender((n) => n + 1);
        if (start && point && onShapeDrawComplete) {
          onShapeDrawComplete(start, point);
        }
        return;
      }

      // Fire onBackgroundClick for a tap (no movement).
      if (!touchDragged && touchStartClient && e.changedTouches.length > 0 && onBackgroundClick) {
        const touch = e.changedTouches[0];
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        const isInteractiveOverlayTarget =
          Boolean(target?.closest('.photo-marker')) || Boolean(target?.closest('.unit-polygon'));
        if (!gestureBecamePinchRef.current && !isInteractiveOverlayTarget) {
          const point = clientToPdfPoint(touch.clientX, touch.clientY);
          if (point) onBackgroundClick(point.x, point.y);
        }
      }
      touchStartDistanceRef.current = null;
      dragStartRef.current = null;
      pinchAnchorRef.current = null;
      touchStartClient = null;
      touchDragged = false;
      touchGestureActiveRef.current = false;
      if (e.touches.length === 0) {
        gestureBecamePinchRef.current = false;
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [
    onScaleChange,
    onPositionChange,
    onBackgroundClick,
    clientToPdfPoint,
    drawingEnabled,
    drawStartPoint,
    interactionState,
    viewerState.scale,
    draftLayerData,
    onLayerChange,
    onShapeDrawComplete,
  ]);

  const currentCropKey = cropRegion ? cropRegion.join(',') : '';
  const renderedContent = lastRenderedRef.current;
  const errorMessage = runtimeQuery.isError
    ? runtimeQuery.error.message
    : documentQuery.isError
      ? documentQuery.error.message
      : renderError;
  const isCurrentContentRendered = Boolean(
    documentQuery.data &&
    renderedContent &&
    renderedContent.doc === documentQuery.data &&
    renderedContent.page === viewerState.currentPage &&
    renderedContent.rotation === viewerState.rotation &&
    renderedContent.cropKey === currentCropKey
  );
  const isLoading =
    runtimeQuery.isLoading ||
    documentQuery.isLoading ||
    isRendering ||
    (!errorMessage && !isCurrentContentRendered);

  const cursor = drawingEnabled
    ? (isDrawingRef.current ? 'crosshair' : 'crosshair')
    : (isDraggingRef.current ? 'grabbing' : 'grab');

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-muted/50"
      style={{ cursor, touchAction: 'none', userSelect: 'none' }}
      onMouseDown={handleMouseDown}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10">
          <PDFLoadingState message={loadingMessage} />
        </div>
      )}

      {errorMessage && (
        <div className="absolute inset-0 z-10">
          <PDFErrorState message={errorMessage} />
        </div>
      )}

      {/* PDF canvas — positioned and scaled via CSS transform */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transformOrigin: '0 0',
          transform: [
            `translate(${viewerState.position.x}px, ${viewerState.position.y}px)`,
            `scale(${viewerState.scale})`,
            flippedHorizontal && pdfDimensions ? `translate(${pdfDimensions.width}px, 0)` : '',
            flippedVertical && pdfDimensions ? `translate(0, ${pdfDimensions.height}px)` : '',
            flippedHorizontal ? 'scaleX(-1)' : '',
            flippedVertical ? 'scaleY(-1)' : '',
          ].filter(Boolean).join(' '),
          boxShadow: pdfDimensions ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
          backgroundColor: 'white',
        }}
      >
        {/* Canvas is always in the DOM so the ref never goes missing */}
        <canvas ref={canvasRef} width={0} height={0} style={{ display: 'block' }} />
      </div>
      {pdfDimensions && (
        <svg
          className="absolute top-0 left-0"
          style={{
            left: `${viewerState.position.x}px`,
            top: `${viewerState.position.y}px`,
            width: `${pdfDimensions.width * viewerState.scale}px`,
            height: `${pdfDimensions.height * viewerState.scale}px`,
          }}
          viewBox={`0 0 ${pdfDimensions.width} ${pdfDimensions.height}`}
          shapeRendering="geometricPrecision"
          textRendering="geometricPrecision"
        >
          {drawStartPoint && drawCurrentPoint && (
            <>
              {drawingShapeType === 'arrow' ? (
                <line
                  x1={drawStartPoint.x}
                  y1={drawStartPoint.y}
                  x2={drawCurrentPoint.x}
                  y2={drawCurrentPoint.y}
                  stroke="#2563eb"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ) : drawingShapeType === 'cloud' ? (
                <path
                  d={getCloudPath(
                    Math.min(drawStartPoint.x, drawCurrentPoint.x),
                    Math.min(drawStartPoint.y, drawCurrentPoint.y),
                    Math.max(8, Math.abs(drawCurrentPoint.x - drawStartPoint.x)),
                    Math.max(8, Math.abs(drawCurrentPoint.y - drawStartPoint.y))
                  )}
                  stroke="#2563eb"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  fill="transparent"
                />
              ) : drawingShapeType === 'pin' ? (
                <g>
                  {(() => {
                    const pinX = drawCurrentPoint.x;
                    const pinY = drawCurrentPoint.y;
                    const size = 24;
                    const left = pinX - size / 2;
                    const top = pinY - size;
                    const pinLeft = left + size * 0.17;
                    const pinWidth = size * 0.66;
                    const cx = pinLeft + pinWidth / 2;
                    const cy = top + size * 0.33;
                    const r = size * 0.14;
                    return (
                      <>
                        <path
                          d={`
                            M ${cx} ${top}
                            C ${pinLeft + pinWidth * 0.16} ${top}, ${pinLeft} ${top + size * 0.26}, ${pinLeft} ${top + size * 0.44}
                            C ${pinLeft} ${top + size * 0.66}, ${pinLeft + pinWidth * 0.24} ${top + size * 0.84}, ${cx} ${pinY}
                            C ${pinLeft + pinWidth * 0.76} ${top + size * 0.84}, ${pinLeft + pinWidth} ${top + size * 0.66}, ${pinLeft + pinWidth} ${top + size * 0.44}
                            C ${pinLeft + pinWidth} ${top + size * 0.26}, ${pinLeft + pinWidth * 0.84} ${top}, ${cx} ${top}
                            Z
                          `}
                          fill="rgba(255,255,255,0.92)"
                          stroke="#2563eb"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          vectorEffect="non-scaling-stroke"
                        />
                        <circle
                          cx={cx}
                          cy={cy}
                          r={r}
                          fill="transparent"
                          stroke="#2563eb"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          vectorEffect="non-scaling-stroke"
                        />
                      </>
                    );
                  })()}
                </g>
              ) : (
                <rect
                  x={Math.min(drawStartPoint.x, drawCurrentPoint.x)}
                  y={Math.min(drawStartPoint.y, drawCurrentPoint.y)}
                  width={Math.max(8, Math.abs(drawCurrentPoint.x - drawStartPoint.x))}
                  height={Math.max(8, Math.abs(drawCurrentPoint.y - drawStartPoint.y))}
                  rx={4}
                  stroke="#2563eb"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  fill="transparent"
                />
              )}
            </>
          )}
          {searchHighlights.map((highlight, index) => (
            <polygon
              key={`search-highlight-${index}`}
              points={highlight.points.map((point) => `${snap(point.x)},${snap(point.y)}`).join(' ')}
              fill={highlight.active ? 'rgba(245, 158, 11, 0.38)' : 'rgba(250, 204, 21, 0.24)'}
              stroke={highlight.active ? '#d97706' : '#ca8a04'}
              strokeWidth={highlight.active ? 2 : 1.25}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              className="pointer-events-none"
            />
          ))}
          {layers.filter((layer) => layer.visible).map((layer) => {
            const layerData = getEffectiveLayerData(layer);

            if (layer.layer_type === 'hyperlink' && layerData.auto_reference_link) {
              const x = Number(layerData.x ?? 0);
              const y = Number(layerData.y ?? 0);
              const width = Math.max(8, Number(layerData.width ?? 24));
              const height = Math.max(8, Number(layerData.height ?? 14));
              const targetPdfFileId = String(layerData.target_pdf_file_id ?? '');
              const targetPageNumber = Number(layerData.target_page_number ?? 1);
              return (
                <g key={layer.id}>
                  <rect
                    x={snap(x)}
                    y={snap(y)}
                    width={snap(width)}
                    height={snap(height)}
                    rx={2}
                    fill="rgba(251,191,36,0.22)"
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                    strokeDasharray="3 2"
                    vectorEffect="non-scaling-stroke"
                    className="cursor-pointer"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (targetPdfFileId) {
                        onHyperlinkNavigate?.({
                          pdfFileId: targetPdfFileId,
                          pageNumber: targetPageNumber,
                          referenceToken: undefined,
                        });
                      }
                    }}
                  />
                </g>
              );
            }

            if (layer.layer_type === 'text' || layer.layer_type === 'annotation') {
              const x = Number(layerData.x ?? 20);
              const y = Number(layerData.y ?? 20);
              const width = Math.max(60, Number(layerData.width ?? 100));
              const height = Math.max(24, Number(layerData.height ?? 32));
              const isSelectedEditableText = selectedContextLayer?.id === layer.id && selectedContextIsText;
              const fontSize = Math.max(
                8,
                Number(isSelectedEditableText ? selectedTextFontSize ?? layerData.fontSize ?? 14 : layerData.fontSize ?? 14)
              );
              const textValue = String(
                isSelectedEditableText ? selectedTextDraft ?? layerData.text ?? '' : layerData.text ?? ''
              );
              const textLines = wrapTextToWidth(textValue, width, fontSize);
              return (
                <g
                  key={layer.id}
                  className="cursor-move"
                  onMouseDown={(e) => startMoveInteraction(e, layer, layerData, 'box')}
                  onTouchStart={(e) => {
                    if (e.touches.length !== 1) return;
                    const touch = e.touches[0];
                    startMoveInteraction(
                      {
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                        metaKey: false,
                        ctrlKey: false,
                        stopPropagation: () => e.stopPropagation(),
                      },
                      layer,
                      layerData,
                      'box'
                    );
                    e.preventDefault();
                  }}
                >
                  <rect
                    x={snap(x)}
                    y={snap(y)}
                    width={snap(width)}
                    height={snap(height)}
                    rx={2}
                    stroke={isLayerSelected(layer.id) ? '#2563eb' : '#64748b'}
                    strokeWidth={isLayerSelected(layer.id) ? 1.75 : 1.25}
                    strokeDasharray={isLayerSelected(layer.id) ? undefined : '5 3'}
                    vectorEffect="non-scaling-stroke"
                    fill="transparent"
                  />
                  {!isSelectedEditableText && (
                    <text
                      x={snap(x + 6)}
                      y={snap(y + Math.min(fontSize + 4, height - 6))}
                      fill={isLayerSelected(layer.id) ? '#2563eb' : '#dc2626'}
                      fontSize={fontSize}
                      vectorEffect="non-scaling-stroke"
                    >
                      {textLines.map((line, index) => (
                        <tspan
                          key={`${layer.id}-line-${index}`}
                          x={snap(x + 6)}
                          dy={index === 0 ? 0 : fontSize * 1.2}
                        >
                          {line || ' '}
                        </tspan>
                      ))}
                    </text>
                  )}
                  {isLayerSelected(layer.id) && (
                    <>
                      {(() => {
                        const frame = expandBounds({ x, y, width, height });
                        return (
                          <>
                      <rect
                        x={frame.x}
                        y={frame.y}
                        width={frame.width}
                        height={frame.height}
                        stroke="#2563eb"
                        strokeWidth={1.5}
                        vectorEffect="non-scaling-stroke"
                        fill="transparent"
                      />
                      {([
                        { corner: 'tl' as const, cx: frame.x, cy: frame.y },
                        { corner: 'tr' as const, cx: frame.x + frame.width, cy: frame.y },
                        { corner: 'br' as const, cx: frame.x + frame.width, cy: frame.y + frame.height },
                        { corner: 'bl' as const, cx: frame.x, cy: frame.y + frame.height },
                      ]).map((h) => (
                        <circle
                          key={h.corner}
                          cx={h.cx}
                          cy={h.cy}
                          r={5}
                          fill="#fff"
                          stroke="#2563eb"
                          strokeWidth={2}
                          className="cursor-nwse-resize"
                          onMouseDown={(e) => startResizeInteraction(e, layer.id, layerData, h.corner, 'box')}
                          onTouchStart={(e) => {
                            if (e.touches.length !== 1) return;
                            const touch = e.touches[0];
                            startResizeInteraction(
                              {
                                clientX: touch.clientX,
                                clientY: touch.clientY,
                                stopPropagation: () => e.stopPropagation(),
                              },
                              layer.id,
                              layerData,
                              h.corner,
                              'box'
                            );
                            e.preventDefault();
                          }}
                        />
                      ))}
                          </>
                        );
                      })()}
                    </>
                  )}
                </g>
              );
            }
            if (layer.layer_type === 'shape') {
                const shapeType = String(layerData.shapeType ?? 'cloud');
                if (shapeType === 'arrow') {
                  const x1 = Number(layerData.x1 ?? 20);
                  const y1 = Number(layerData.y1 ?? 20);
                  const x2 = Number(layerData.x2 ?? 140);
                  const y2 = Number(layerData.y2 ?? 20);
                  return (
                    <g
                      key={layer.id}
                      className="cursor-move"
                      onMouseDown={(e) => startMoveInteraction(e, layer, layerData, 'line')}
                      onTouchStart={(e) => {
                        if (e.touches.length !== 1) return;
                        const touch = e.touches[0];
                        startMoveInteraction(
                          {
                            clientX: touch.clientX,
                            clientY: touch.clientY,
                            metaKey: false,
                            ctrlKey: false,
                            stopPropagation: () => e.stopPropagation(),
                          },
                          layer,
                          layerData,
                          'line'
                        );
                        e.preventDefault();
                      }}
                    >
                      <defs>
                        <marker
                          id={`arrowhead-${layer.id}`}
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                        >
                          <polygon
                            points="0 0, 10 3.5, 0 7"
                            fill={isLayerSelected(layer.id) ? '#2563eb' : '#ef4444'}
                          />
                        </marker>
                      </defs>
                      <line
                        x1={snap(x1)}
                        y1={snap(y1)}
                        x2={snap(x2)}
                        y2={snap(y2)}
                        stroke={isLayerSelected(layer.id) ? '#2563eb' : '#ef4444'}
                        strokeWidth={isLayerSelected(layer.id) ? 3 : 2}
                        markerEnd={`url(#arrowhead-${layer.id})`}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                      />
                      {isLayerSelected(layer.id) && (
                        <>
                          {(() => {
                            const b = getLayerBounds(layerData, 'line');
                            const handles: Array<{ corner: ResizeCorner; cx: number; cy: number }> = [
                              { corner: 'tl', cx: b.x, cy: b.y },
                              { corner: 'tr', cx: b.x + b.width, cy: b.y },
                              { corner: 'br', cx: b.x + b.width, cy: b.y + b.height },
                              { corner: 'bl', cx: b.x, cy: b.y + b.height },
                            ];
                            return (
                              <>
                                {(() => {
                                  const frame = snapRect(
                                    expandBounds(b).x,
                                    expandBounds(b).y,
                                    expandBounds(b).width,
                                    expandBounds(b).height
                                  );
                                  return (
                                <rect
                                  x={frame.x}
                                  y={frame.y}
                                  width={frame.width}
                                  height={frame.height}
                                  stroke="#2563eb"
                                  strokeWidth={1.5}
                                  vectorEffect="non-scaling-stroke"
                                  fill="transparent"
                                />
                                  );
                                })()}
                                {handles.map((h) => {
                                  const frame = expandBounds(b);
                                  const mapped = {
                                    tl: { cx: frame.x, cy: frame.y },
                                    tr: { cx: frame.x + frame.width, cy: frame.y },
                                    br: { cx: frame.x + frame.width, cy: frame.y + frame.height },
                                    bl: { cx: frame.x, cy: frame.y + frame.height },
                                  }[h.corner];
                                  return (
                                  <circle
                                    key={h.corner}
                                    cx={mapped.cx}
                                    cy={mapped.cy}
                                    r={5}
                                    fill="#fff"
                                    stroke="#2563eb"
                                    strokeWidth={2}
                                    className="cursor-nwse-resize"
                                    onMouseDown={(e) => startResizeInteraction(e, layer.id, layerData, h.corner, 'line')}
                                    onTouchStart={(e) => {
                                      if (e.touches.length !== 1) return;
                                      const touch = e.touches[0];
                                      startResizeInteraction(
                                        {
                                          clientX: touch.clientX,
                                          clientY: touch.clientY,
                                          stopPropagation: () => e.stopPropagation(),
                                        },
                                        layer.id,
                                        layerData,
                                        h.corner,
                                        'line'
                                      );
                                      e.preventDefault();
                                    }}
                                  />
                                  );
                                })}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </g>
                  );
                }
                if (shapeType === 'cloud') {
                  const x = Number(layerData.x ?? 20);
                  const y = Number(layerData.y ?? 20);
                  const width = Number(layerData.width ?? 120);
                  const height = Number(layerData.height ?? 80);
                  return (
                    <g
                      key={layer.id}
                      className="cursor-move"
                      onMouseDown={(e) => startMoveInteraction(e, layer, layerData, 'box')}
                      onTouchStart={(e) => {
                        if (e.touches.length !== 1) return;
                        const touch = e.touches[0];
                        startMoveInteraction(
                          {
                            clientX: touch.clientX,
                            clientY: touch.clientY,
                            metaKey: false,
                            ctrlKey: false,
                            stopPropagation: () => e.stopPropagation(),
                          },
                          layer,
                          layerData,
                          'box'
                        );
                        e.preventDefault();
                      }}
                    >
                      <path
                        stroke={isLayerSelected(layer.id) ? '#2563eb' : '#ef4444'}
                        strokeWidth={isLayerSelected(layer.id) ? 3 : 2}
                        d={getCloudPath(snap(x), snap(y), snap(width), snap(height))}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                        fill="transparent"
                      />
                      {isLayerSelected(layer.id) && (
                        <>
                          {(() => {
                            const frame = expandBounds({ x, y, width, height });
                            return (
                              <>
                          <rect
                            x={snap(frame.x)}
                            y={snap(frame.y)}
                            width={snap(frame.width)}
                            height={snap(frame.height)}
                            stroke="#2563eb"
                            strokeWidth={1.5}
                            vectorEffect="non-scaling-stroke"
                            fill="transparent"
                          />
                          {([
                            { corner: 'tl' as const, cx: frame.x, cy: frame.y },
                            { corner: 'tr' as const, cx: frame.x + frame.width, cy: frame.y },
                            { corner: 'br' as const, cx: frame.x + frame.width, cy: frame.y + frame.height },
                            { corner: 'bl' as const, cx: frame.x, cy: frame.y + frame.height },
                          ]).map((h) => (
                            <circle
                              key={h.corner}
                              cx={h.cx}
                              cy={h.cy}
                              r={5}
                              fill="#fff"
                              stroke="#2563eb"
                              strokeWidth={2}
                              className="cursor-nwse-resize"
                              onMouseDown={(e) => startResizeInteraction(e, layer.id, layerData, h.corner, 'box')}
                              onTouchStart={(e) => {
                                if (e.touches.length !== 1) return;
                                const touch = e.touches[0];
                                startResizeInteraction(
                                  {
                                    clientX: touch.clientX,
                                    clientY: touch.clientY,
                                    stopPropagation: () => e.stopPropagation(),
                                  },
                                  layer.id,
                                  layerData,
                                  h.corner,
                                  'box'
                                );
                                e.preventDefault();
                              }}
                            />
                          ))}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </g>
                  );
                }
                if (shapeType === 'pin') {
                  const x = Number(layerData.x ?? 20);
                  const y = Number(layerData.y ?? 20);
                  const width = Math.max(12, Number(layerData.width ?? 24));
                  const height = Math.max(12, Number(layerData.height ?? 24));
                  const pinLeft = x + width * 0.17;
                  const pinWidth = width * 0.66;
                  const cx = pinLeft + pinWidth / 2;
                  const top = y;
                  const pinBottomY = y + height;
                  const cy = top + height * 0.33;
                  return (
                    <g
                      key={layer.id}
                      className="cursor-move"
                      onMouseDown={(e) => startMoveInteraction(e, layer, layerData, 'box')}
                      onTouchStart={(e) => {
                        if (e.touches.length !== 1) return;
                        const touch = e.touches[0];
                        startMoveInteraction(
                          {
                            clientX: touch.clientX,
                            clientY: touch.clientY,
                            metaKey: false,
                            ctrlKey: false,
                            stopPropagation: () => e.stopPropagation(),
                          },
                          layer,
                          layerData,
                          'box'
                        );
                        e.preventDefault();
                      }}
                    >
                      <path
                        d={`
                          M ${cx} ${top}
                          C ${pinLeft + pinWidth * 0.16} ${top}, ${pinLeft} ${top + height * 0.26}, ${pinLeft} ${top + height * 0.44}
                          C ${pinLeft} ${top + height * 0.66}, ${pinLeft + pinWidth * 0.24} ${top + height * 0.84}, ${cx} ${pinBottomY}
                          C ${pinLeft + pinWidth * 0.76} ${top + height * 0.84}, ${pinLeft + pinWidth} ${top + height * 0.66}, ${pinLeft + pinWidth} ${top + height * 0.44}
                          C ${pinLeft + pinWidth} ${top + height * 0.26}, ${pinLeft + pinWidth * 0.84} ${top}, ${cx} ${top}
                          Z
                        `}
                        fill="rgba(255,255,255,0.95)"
                        stroke={isLayerSelected(layer.id) ? '#2563eb' : '#ef4444'}
                        strokeWidth={isLayerSelected(layer.id) ? 3 : 2}
                        vectorEffect="non-scaling-stroke"
                      />
                      <circle
                        cx={cx}
                        cy={cy}
                        r={Math.max(2.5, Math.min(width, height) * 0.14)}
                        fill="transparent"
                        stroke={isLayerSelected(layer.id) ? '#2563eb' : '#ef4444'}
                        strokeWidth={isLayerSelected(layer.id) ? 2.25 : 1.75}
                        vectorEffect="non-scaling-stroke"
                      />
                      {isLayerSelected(layer.id) && (
                        <>
                          {(() => {
                            const frame = expandBounds({ x, y, width, height });
                            return (
                              <>
                                <rect
                                  x={snap(frame.x)}
                                  y={snap(frame.y)}
                                  width={snap(frame.width)}
                                  height={snap(frame.height)}
                                  stroke="#2563eb"
                                  strokeWidth={1.5}
                                  vectorEffect="non-scaling-stroke"
                                  fill="transparent"
                                />
                                {([
                                  { corner: 'tl' as const, cx: frame.x, cy: frame.y },
                                  { corner: 'tr' as const, cx: frame.x + frame.width, cy: frame.y },
                                  { corner: 'br' as const, cx: frame.x + frame.width, cy: frame.y + frame.height },
                                  { corner: 'bl' as const, cx: frame.x, cy: frame.y + frame.height },
                                ]).map((h) => (
                                  <circle
                                    key={h.corner}
                                    cx={h.cx}
                                    cy={h.cy}
                                    r={5}
                                    fill="#fff"
                                    stroke="#2563eb"
                                    strokeWidth={2}
                                    className="cursor-nwse-resize"
                                    onMouseDown={(e) => startResizeInteraction(e, layer.id, layerData, h.corner, 'box')}
                                    onTouchStart={(e) => {
                                      if (e.touches.length !== 1) return;
                                      const touch = e.touches[0];
                                      startResizeInteraction(
                                        {
                                          clientX: touch.clientX,
                                          clientY: touch.clientY,
                                          stopPropagation: () => e.stopPropagation(),
                                        },
                                        layer.id,
                                        layerData,
                                        h.corner,
                                        'box'
                                      );
                                      e.preventDefault();
                                    }}
                                  />
                                ))}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </g>
                  );
                }
                return (
                  <g
                    key={layer.id}
                    className="cursor-move"
                    onMouseDown={(e) => startMoveInteraction(e, layer, layerData, 'box')}
                    onTouchStart={(e) => {
                      if (e.touches.length !== 1) return;
                      const touch = e.touches[0];
                      startMoveInteraction(
                        {
                          clientX: touch.clientX,
                          clientY: touch.clientY,
                          metaKey: false,
                          ctrlKey: false,
                          stopPropagation: () => e.stopPropagation(),
                        },
                        layer,
                        layerData,
                        'box'
                      );
                      e.preventDefault();
                    }}
                  >
                    <rect
                      x={snap(Number(layerData.x ?? 20))}
                      y={snap(Number(layerData.y ?? 20))}
                      width={snap(Number(layerData.width ?? 120))}
                      height={snap(Number(layerData.height ?? 80))}
                      rx={4}
                      stroke={isLayerSelected(layer.id) ? '#2563eb' : '#ef4444'}
                      strokeWidth={isLayerSelected(layer.id) ? 3 : 2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                      fill="transparent"
                    />
                    {isLayerSelected(layer.id) && (
                      <>
                        {(() => {
                          const x = Number(layerData.x ?? 20);
                          const y = Number(layerData.y ?? 20);
                          const width = Number(layerData.width ?? 120);
                          const height = Number(layerData.height ?? 80);
                          return (
                            <>
                              <rect
                                x={snap(expandBounds({ x, y, width, height }).x)}
                                y={snap(expandBounds({ x, y, width, height }).y)}
                                width={snap(expandBounds({ x, y, width, height }).width)}
                                height={snap(expandBounds({ x, y, width, height }).height)}
                                stroke="#2563eb"
                                strokeWidth={1.5}
                                vectorEffect="non-scaling-stroke"
                                fill="transparent"
                              />
                              {([
                                { corner: 'tl' as const },
                                { corner: 'tr' as const },
                                { corner: 'br' as const },
                                { corner: 'bl' as const },
                              ]).map((h) => {
                                const frame = expandBounds({ x, y, width, height });
                                const mapped = {
                                  tl: { cx: frame.x, cy: frame.y },
                                  tr: { cx: frame.x + frame.width, cy: frame.y },
                                  br: { cx: frame.x + frame.width, cy: frame.y + frame.height },
                                  bl: { cx: frame.x, cy: frame.y + frame.height },
                                }[h.corner];
                                return (
                                <circle
                                  key={h.corner}
                                  cx={mapped.cx}
                                  cy={mapped.cy}
                                  r={5}
                                  fill="#fff"
                                  stroke="#2563eb"
                                  strokeWidth={2}
                                  className="cursor-nwse-resize"
                                  onMouseDown={(e) => startResizeInteraction(e, layer.id, layerData, h.corner, 'box')}
                                  onTouchStart={(e) => {
                                    if (e.touches.length !== 1) return;
                                    const touch = e.touches[0];
                                    startResizeInteraction(
                                      {
                                        clientX: touch.clientX,
                                        clientY: touch.clientY,
                                        stopPropagation: () => e.stopPropagation(),
                                      },
                                      layer.id,
                                      layerData,
                                      h.corner,
                                      'box'
                                    );
                                    e.preventDefault();
                                  }}
                                />
                                );
                              })}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </g>
                );
              }
              if (layer.layer_type === 'measurement') {
                const x1 = Number(layerData.x1 ?? 20);
                const y1 = Number(layerData.y1 ?? 20);
                const x2 = Number(layerData.x2 ?? 140);
                const y2 = Number(layerData.y2 ?? 20);
                return (
                  <g
                    key={layer.id}
                    className="cursor-move"
                    onMouseDown={(e) => startMoveInteraction(e, layer, layerData, 'line')}
                    onTouchStart={(e) => {
                      if (e.touches.length !== 1) return;
                      const touch = e.touches[0];
                      startMoveInteraction(
                        {
                          clientX: touch.clientX,
                          clientY: touch.clientY,
                          metaKey: false,
                          ctrlKey: false,
                          stopPropagation: () => e.stopPropagation(),
                        },
                        layer,
                        layerData,
                        'line'
                      );
                      e.preventDefault();
                    }}
                  >
                    <line
                      x1={snap(x1)}
                      y1={snap(y1)}
                      x2={snap(x2)}
                      y2={snap(y2)}
                      stroke={isLayerSelected(layer.id) ? '#2563eb' : '#16a34a'}
                      strokeWidth={isLayerSelected(layer.id) ? 3 : 2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                    <text
                      x={snap((x1 + x2) / 2)}
                      y={snap((y1 + y2) / 2 - 6)}
                      fill={isLayerSelected(layer.id) ? '#2563eb' : '#16a34a'}
                      fontSize={12}
                      textAnchor="middle"
                    >
                      {String(layerData.label ?? 'Measurement')}
                    </text>
                    {isLayerSelected(layer.id) && (
                      <>
                        {(() => {
                          const b = getLayerBounds(layerData, 'line');
                          const handles: Array<{ corner: ResizeCorner; cx: number; cy: number }> = [
                            { corner: 'tl', cx: b.x, cy: b.y },
                            { corner: 'tr', cx: b.x + b.width, cy: b.y },
                            { corner: 'br', cx: b.x + b.width, cy: b.y + b.height },
                            { corner: 'bl', cx: b.x, cy: b.y + b.height },
                          ];
                          return (
                            <>
                              <rect
                                x={snap(expandBounds(b).x)}
                                y={snap(expandBounds(b).y)}
                                width={snap(expandBounds(b).width)}
                                height={snap(expandBounds(b).height)}
                                stroke="#2563eb"
                                strokeWidth={1.5}
                                vectorEffect="non-scaling-stroke"
                                fill="transparent"
                              />
                              {handles.map((h) => {
                                const frame = expandBounds(b);
                                const mapped = {
                                  tl: { cx: frame.x, cy: frame.y },
                                  tr: { cx: frame.x + frame.width, cy: frame.y },
                                  br: { cx: frame.x + frame.width, cy: frame.y + frame.height },
                                  bl: { cx: frame.x, cy: frame.y + frame.height },
                                }[h.corner];
                                return (
                                <circle
                                  key={h.corner}
                                  cx={mapped.cx}
                                  cy={mapped.cy}
                                  r={5}
                                  fill="#fff"
                                  stroke="#2563eb"
                                  strokeWidth={2}
                                  className="cursor-nwse-resize"
                                  onMouseDown={(e) => startResizeInteraction(e, layer.id, layerData, h.corner, 'line')}
                                  onTouchStart={(e) => {
                                    if (e.touches.length !== 1) return;
                                    const touch = e.touches[0];
                                    startResizeInteraction(
                                      {
                                        clientX: touch.clientX,
                                        clientY: touch.clientY,
                                        stopPropagation: () => e.stopPropagation(),
                                      },
                                      layer.id,
                                      layerData,
                                      h.corner,
                                      'line'
                                    );
                                    e.preventDefault();
                                  }}
                                />
                                );
                              })}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </g>
                );
              }
              return null;
            })}
          {observationCallouts.map((callout) => (
                <g key={`obs-link-${callout.layerId}`} pointerEvents="none">
                  <defs>
                    <marker
                      id={callout.markerId}
                      markerWidth="8"
                      markerHeight="6"
                      refX="7"
                      refY="3"
                      orient="auto"
                    >
                      <polygon points="0 0, 8 3, 0 6" fill="#2563eb" />
                    </marker>
                  </defs>
                  <line
                    x1={snap(callout.lineStartX)}
                    y1={snap(callout.lineStartY)}
                    x2={snap(callout.lineEndX)}
                    y2={snap(callout.lineEndY)}
                    stroke="#2563eb"
                    strokeWidth={1.75}
                    markerEnd={`url(#${callout.markerId})`}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <rect
                    x={snap(callout.labelX)}
                    y={snap(callout.labelY)}
                    width={snap(callout.labelWidth)}
                    height={snap(callout.labelHeight)}
                    rx={3}
                    fill="#ffffff"
                    stroke="#2563eb"
                    strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={snap(callout.labelX + 8)}
                    y={snap(callout.labelY + 16)}
                    fill="#1d4ed8"
                    fontSize={12}
                    fontWeight={600}
                  >
                    {callout.labelText}
                  </text>
                </g>
              ))}
          {punchCallouts.map((callout) => (
                <g key={`punch-link-${callout.layerId}`} pointerEvents="none">
                  <defs>
                    <marker
                      id={callout.markerId}
                      markerWidth="8"
                      markerHeight="6"
                      refX="7"
                      refY="3"
                      orient="auto"
                    >
                      <polygon points="0 0, 8 3, 0 6" fill="#2563eb" />
                    </marker>
                  </defs>
                  <line
                    x1={snap(callout.lineStartX)}
                    y1={snap(callout.lineStartY)}
                    x2={snap(callout.lineEndX)}
                    y2={snap(callout.lineEndY)}
                    stroke="#2563eb"
                    strokeWidth={1.75}
                    markerEnd={`url(#${callout.markerId})`}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <rect
                    x={snap(callout.labelX)}
                    y={snap(callout.labelY)}
                    width={snap(callout.labelWidth)}
                    height={snap(callout.labelHeight)}
                    rx={3}
                    fill="#ffffff"
                    stroke="#2563eb"
                    strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={snap(callout.labelX + 8)}
                    y={snap(callout.labelY + 16)}
                    fill="#1d4ed8"
                    fontSize={12}
                    fontWeight={600}
                  >
                    {callout.labelText}
                  </text>
                </g>
              ))}
          {submittalCallouts.map((callout) => (
                <g key={`submittal-link-${callout.layerId}`} pointerEvents="none">
                  <defs>
                    <marker
                      id={callout.markerId}
                      markerWidth="8"
                      markerHeight="6"
                      refX="7"
                      refY="3"
                      orient="auto"
                    >
                      <polygon points="0 0, 8 3, 0 6" fill="#7c3aed" />
                    </marker>
                  </defs>
                  <line
                    x1={snap(callout.lineStartX)}
                    y1={snap(callout.lineStartY)}
                    x2={snap(callout.lineEndX)}
                    y2={snap(callout.lineEndY)}
                    stroke="#7c3aed"
                    strokeWidth={1.75}
                    markerEnd={`url(#${callout.markerId})`}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <rect
                    x={snap(callout.labelX)}
                    y={snap(callout.labelY)}
                    width={snap(callout.labelWidth)}
                    height={snap(callout.labelHeight)}
                    rx={3}
                    fill="#ffffff"
                    stroke="#7c3aed"
                    strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={snap(callout.labelX + 8)}
                    y={snap(callout.labelY + 16)}
                    fill="#6d28d9"
                    fontSize={12}
                    fontWeight={600}
                  >
                    {callout.labelText}
                  </text>
                </g>
              ))}
          {rfiCallouts.map((callout) => (
                <g key={`rfi-link-${callout.layerId}`} pointerEvents="none">
                  <defs>
                    <marker
                      id={callout.markerId}
                      markerWidth="8"
                      markerHeight="6"
                      refX="7"
                      refY="3"
                      orient="auto"
                    >
                      <polygon points="0 0, 8 3, 0 6" fill="#0f766e" />
                    </marker>
                  </defs>
                  <line
                    x1={snap(callout.lineStartX)}
                    y1={snap(callout.lineStartY)}
                    x2={snap(callout.lineEndX)}
                    y2={snap(callout.lineEndY)}
                    stroke="#0f766e"
                    strokeWidth={1.75}
                    markerEnd={`url(#${callout.markerId})`}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <rect
                    x={snap(callout.labelX)}
                    y={snap(callout.labelY)}
                    width={snap(callout.labelWidth)}
                    height={snap(callout.labelHeight)}
                    rx={3}
                    fill="#ffffff"
                    stroke="#0f766e"
                    strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={snap(callout.labelX + 8)}
                    y={snap(callout.labelY + 16)}
                    fill="#0f766e"
                    fontSize={12}
                    fontWeight={600}
                  >
                    {callout.labelText}
                  </text>
                </g>
              ))}
          </svg>
        )}
      <TooltipProvider delayDuration={0}>
        {observationCallouts.map((callout) => {
          const panelLeft = snap(viewerState.position.x + callout.labelX * viewerState.scale);
          const panelTop = snap(viewerState.position.y + callout.labelY * viewerState.scale);
          const panelWidth = snap(callout.labelWidth * viewerState.scale);
          const panelHeight = snap(callout.labelHeight * viewerState.scale);
          const obs = callout.observation;
          const assigneeName = obs?.assignee
            ? `${obs.assignee.first_name ?? ''} ${obs.assignee.last_name ?? ''}`.trim() || obs.assignee.email || 'Unassigned'
            : 'Unassigned';

          return (
            <div
              key={`obs-link-trigger-${callout.layerId}`}
              className="absolute z-20"
              style={{ left: `${panelLeft}px`, top: `${panelTop}px`, width: `${panelWidth}px`, height: `${panelHeight}px` }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-full w-full rounded-sm bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (obs?.id) onObservationLabelClick?.(obs.id);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="w-80 bg-popover text-popover-foreground border px-0 py-0 shadow-lg">
                  <div className="space-y-2 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        {obs?.is_private && <Lock className="h-3 w-3 text-muted-foreground" />}
                        <span className="text-xs font-mono text-muted-foreground">{callout.labelText}</span>
                        <span className="truncate text-sm font-medium">{obs?.title ?? 'Linked observation'}</span>
                      </div>
                      {obs?.status && (
                        <Badge variant="secondary" className={`text-xs ${OBS_STATUS_COLORS[obs.status] ?? ''}`}>
                          {OBS_STATUS_LABELS[obs.status as keyof typeof OBS_STATUS_LABELS] ?? obs.status}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>Priority</span>
                      </div>
                      <div>
                        {obs?.priority ? (
                          <Badge variant="outline" className={`text-xs ${OBS_PRIORITY_COLORS[obs.priority] ?? ''}`}>
                            {OBS_PRIORITY_LABELS[obs.priority as keyof typeof OBS_PRIORITY_LABELS] ?? obs.priority}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <User className="h-3.5 w-3.5" />
                        <span>Assignee</span>
                      </div>
                      <div className="truncate">{assigneeName}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Due Date</span>
                      </div>
                      <div>{obs?.due_date ? new Date(obs.due_date).toLocaleDateString() : '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Tag className="h-3.5 w-3.5" />
                        <span>Type</span>
                      </div>
                      <div className="truncate">{obs?.observation_type?.name ?? '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>Location</span>
                      </div>
                      <div className="truncate">{obs?.location?.name ?? '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Wrench className="h-3.5 w-3.5" />
                        <span>Trade</span>
                      </div>
                      <div className="truncate">{obs?.trade?.name ?? '-'}</div>
                    </div>
                    {obs?.created_at && (
                      <p className="text-[11px] text-muted-foreground">
                        Created {formatDistanceToNow(new Date(obs.created_at), { addSuffix: true })}
                      </p>
                    )}
                    {obs?.description && (
                      <p className="line-clamp-3 text-xs text-muted-foreground">{obs.description}</p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
        {punchCallouts.map((callout) => {
          const panelLeft = snap(viewerState.position.x + callout.labelX * viewerState.scale);
          const panelTop = snap(viewerState.position.y + callout.labelY * viewerState.scale);
          const panelWidth = snap(callout.labelWidth * viewerState.scale);
          const panelHeight = snap(callout.labelHeight * viewerState.scale);
          const item = callout.punchItem;
          const assigneeName = item?.assignee
            ? `${item.assignee.first_name ?? ''} ${item.assignee.last_name ?? ''}`.trim() || item.assignee.email || 'Unassigned'
            : 'Unassigned';

          return (
            <div
              key={`punch-link-trigger-${callout.layerId}`}
              className="absolute z-20"
              style={{ left: `${panelLeft}px`, top: `${panelTop}px`, width: `${panelWidth}px`, height: `${panelHeight}px` }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-full w-full rounded-sm bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item?.id) onPunchLabelClick?.(item.id);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="w-80 bg-popover text-popover-foreground border px-0 py-0 shadow-lg">
                  <div className="space-y-2 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        {item?.is_private && <Lock className="h-3 w-3 text-muted-foreground" />}
                        <span className="text-xs font-mono text-muted-foreground">PUNCH</span>
                        <span className="truncate text-sm font-medium">{item?.title ?? 'Linked punch item'}</span>
                      </div>
                      {item?.status && (
                        <Badge variant="secondary" className={`text-xs ${PUNCH_STATUS_COLORS[item.status] ?? ''}`}>
                          {PUNCH_STATUS_LABELS[item.status as keyof typeof PUNCH_STATUS_LABELS] ?? item.status}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>Priority</span>
                      </div>
                      <div>
                        {item?.priority ? (
                          <Badge variant="outline" className={`text-xs ${PUNCH_PRIORITY_COLORS[item.priority] ?? ''}`}>
                            {PUNCH_PRIORITY_LABELS[item.priority as keyof typeof PUNCH_PRIORITY_LABELS] ?? item.priority}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <User className="h-3.5 w-3.5" />
                        <span>Assignee</span>
                      </div>
                      <div className="truncate">{assigneeName}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Due Date</span>
                      </div>
                      <div>{item?.due_date ? new Date(item.due_date).toLocaleDateString() : '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Tag className="h-3.5 w-3.5" />
                        <span>Type</span>
                      </div>
                      <div className="truncate">{item?.item_type?.name ?? '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>Location</span>
                      </div>
                      <div className="truncate">{item?.location?.name ?? '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Wrench className="h-3.5 w-3.5" />
                        <span>Trade</span>
                      </div>
                      <div className="truncate">{item?.trade?.name ?? '-'}</div>
                    </div>
                    {item?.created_at && (
                      <p className="text-[11px] text-muted-foreground">
                        Created {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                      </p>
                    )}
                    {item?.description && (
                      <p className="line-clamp-3 text-xs text-muted-foreground">{item.description}</p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
        {submittalCallouts.map((callout) => {
          const panelLeft = snap(viewerState.position.x + callout.labelX * viewerState.scale);
          const panelTop = snap(viewerState.position.y + callout.labelY * viewerState.scale);
          const panelWidth = snap(callout.labelWidth * viewerState.scale);
          const panelHeight = snap(callout.labelHeight * viewerState.scale);
          const item = callout.submittal;

          return (
            <div
              key={`submittal-link-trigger-${callout.layerId}`}
              className="absolute z-20"
              style={{ left: `${panelLeft}px`, top: `${panelTop}px`, width: `${panelWidth}px`, height: `${panelHeight}px` }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-full w-full rounded-sm bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item?.id) onSubmittalLabelClick?.(item.id);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="w-80 bg-popover text-popover-foreground border px-0 py-0 shadow-lg">
                  <div className="space-y-2 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">{item?.submittal_number ?? callout.labelText}</span>
                        <span className="truncate text-sm font-medium">{item?.title ?? 'Linked submittal'}</span>
                      </div>
                      {item?.status && (
                        <Badge variant="secondary" className={`text-xs ${SUBMITTAL_STATUS_COLORS[item.status] ?? ''}`}>
                          {SUBMITTAL_STATUS_LABELS[item.status as keyof typeof SUBMITTAL_STATUS_LABELS] ?? item.status}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>Priority</span>
                      </div>
                      <div>
                        {item?.priority ? (
                          <Badge variant="outline" className={`text-xs ${SUBMITTAL_PRIORITY_COLORS[item.priority] ?? ''}`}>
                            {SUBMITTAL_PRIORITY_LABELS[item.priority as keyof typeof SUBMITTAL_PRIORITY_LABELS] ?? item.priority}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Due Date</span>
                      </div>
                      <div>{item?.due_date ? new Date(item.due_date).toLocaleDateString() : '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Tag className="h-3.5 w-3.5" />
                        <span>Revision</span>
                      </div>
                      <div>{item?.revision_number ?? '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Wrench className="h-3.5 w-3.5" />
                        <span>Trade</span>
                      </div>
                      <div className="truncate">{item?.trade?.name ?? '-'}</div>
                    </div>
                    {item?.responsible_contractor && (
                      <p className="text-xs text-muted-foreground">Contractor: {item.responsible_contractor}</p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
        {rfiCallouts.map((callout) => {
          const panelLeft = snap(viewerState.position.x + callout.labelX * viewerState.scale);
          const panelTop = snap(viewerState.position.y + callout.labelY * viewerState.scale);
          const panelWidth = snap(callout.labelWidth * viewerState.scale);
          const panelHeight = snap(callout.labelHeight * viewerState.scale);
          const item = callout.rfi;

          return (
            <div
              key={`rfi-link-trigger-${callout.layerId}`}
              className="absolute z-20"
              style={{ left: `${panelLeft}px`, top: `${panelTop}px`, width: `${panelWidth}px`, height: `${panelHeight}px` }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-full w-full rounded-sm bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item?.id) onRfiLabelClick?.(item.id);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="w-80 bg-popover text-popover-foreground border px-0 py-0 shadow-lg">
                  <div className="space-y-2 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="text-xs font-mono text-muted-foreground">{item?.rfi_number ?? callout.labelText}</span>
                        <span className="truncate text-sm font-medium">{item?.title ?? 'Linked RFI'}</span>
                      </div>
                      {item?.status && (
                        <Badge variant="secondary" className={`text-xs ${RFI_STATUS_COLORS[item.status] ?? ''}`}>
                          {RFI_STATUS_LABELS[item.status as keyof typeof RFI_STATUS_LABELS] ?? item.status}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>Priority</span>
                      </div>
                      <div>
                        {item?.priority ? (
                          <Badge variant="outline" className={`text-xs ${RFI_PRIORITY_COLORS[item.priority] ?? ''}`}>
                            {RFI_PRIORITY_LABELS[item.priority as keyof typeof RFI_PRIORITY_LABELS] ?? item.priority}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Due Date</span>
                      </div>
                      <div>{item?.due_date ? new Date(item.due_date).toLocaleDateString() : '-'}</div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Wrench className="h-3.5 w-3.5" />
                        <span>Trade</span>
                      </div>
                      <div className="truncate">{item?.trade?.name ?? '-'}</div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
      </TooltipProvider>
        {selectedTextBox && selectedContextLayer && (
          <textarea
            ref={textEditorRef}
            value={selectedTextDraft ?? String(selectedContextLayerData?.text ?? '')}
            onChange={(e) => onSelectedTextDraftChange?.(e.target.value)}
            onBlur={() => onSelectedTextCommit?.()}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                onSelectedTextCommit?.();
              }
            }}
            className="absolute resize-none border-0 bg-transparent px-1.5 py-1 text-foreground outline-none focus:outline-none"
            style={{
              left: `${snap(viewerState.position.x + selectedTextBox.x * viewerState.scale)}px`,
              top: `${snap(viewerState.position.y + selectedTextBox.y * viewerState.scale)}px`,
              width: `${snap(selectedTextBox.width * viewerState.scale)}px`,
              height: `${snap(selectedTextBox.height * viewerState.scale)}px`,
              fontSize: `${Math.max(8, Number(selectedTextFontSize ?? 14)) * viewerState.scale}px`,
              lineHeight: 1.2,
              background: 'transparent',
              boxShadow: 'none',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          />
        )}
      {selectedContextLayer && selectedContextMenuPosition && (
        <div
          className="absolute z-30"
          style={{
            left: `${selectedContextMenuPosition.left}px`,
            top: `${selectedContextMenuPosition.top}px`,
            transform: 'translate(-50%, -100%)',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex max-w-[calc(100vw-1rem)] items-center gap-1 rounded-md border bg-card px-1 py-1 shadow-sm">
            {selectedContextIsText && (
              <div className="flex items-center gap-1 rounded border border-border/70 px-1 py-0.5">
                <span className="text-[10px] text-muted-foreground">Size</span>
                <Input
                  type="number"
                  min={8}
                  max={96}
                  step={1}
                  value={Number.isFinite(selectedTextFontSize) ? selectedTextFontSize : 14}
                  onChange={(e) => {
                    const parsed = Number.parseInt(e.target.value, 10);
                    onSelectedTextFontSizeChange?.(Number.isFinite(parsed) ? parsed : 14);
                  }}
                  onBlur={() => onSelectedTextCommit?.()}
                  className="h-6 w-14 px-1 text-[10px]"
                />
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[10px]"
                >
                  <MoreHorizontal className="mr-1 h-3.5 w-3.5" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onSelect={onSelectedLayerLink}
                  disabled={!onSelectedLayerLink}
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  Link
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={onSelectedLayerDuplicate}
                  disabled={!onSelectedLayerDuplicate}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={onSelectedLayerDelete}
                  disabled={!onSelectedLayerDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </div>
  );
};
